import { useState, useEffect } from 'react';
import { Collapse, Tag, Spin, Alert } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import SynchronizationService from '../../../../services/synchronizationService';

/**
 * Seção expansível que exibe categorias com lançamentos (regime competência)
 * não mapeadas em nenhuma subconta do DRE.
 *
 * Props:
 *   syncId        {string}  — ID do sync
 *   refreshToken  {number}  — incrementar para forçar re-fetch após salvar DRE
 */
export function CategoriasComLancamentoSemDre({ syncId, refreshToken }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    SynchronizationService.getCategoriasComLancamentoSemDre(syncId)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [syncId, refreshToken]);

  if (loading) return <Spin size="small" style={{ margin: '0 0 12px' }} />;
  if (!data || !data.totalCategoriasSemVinculo) return null;

  const grupos = agruparPorPai(data.categorias);

  return (
    <Collapse
      style={{ marginBottom: 16 }}
      items={[{
        key: 'alerta-sem-dre',
        label: (
          <span>
            <WarningOutlined style={{ color: '#D97706', marginRight: 8 }} />
            <strong>
              {data.totalCategoriasSemVinculo}{' '}
              {data.totalCategoriasSemVinculo === 1 ? 'categoria' : 'categorias'} com
              lançamentos sem vínculo no DRE
            </strong>
            <Tag color="warning" style={{ marginLeft: 8 }}>
              {data.totalLancamentos} lançamentos em {data.ano}
            </Tag>
          </span>
        ),
        children: (
          <>
            <Alert
              type="warning"
              showIcon
              message={
                `Usando as regras do DRE por competência, existem ` +
                `${data.totalCategoriasSemVinculo} ` +
                `${data.totalCategoriasSemVinculo === 1 ? 'categoria' : 'categorias'} ` +
                `com lançamentos em ${data.ano} que não estão mapeadas ` +
                `em nenhuma subconta do DRE. Esses valores não aparecerão no relatório.`
              }
              style={{ marginBottom: 12 }}
            />
            {grupos.map((grupo) => (
              <div key={grupo.pai} style={{ marginBottom: 10 }}>
                {grupo.descricaoPai && (
                  <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 4, fontWeight: 500 }}>
                    {grupo.descricaoPai}
                  </div>
                )}
                {grupo.categorias.map((cat) => (
                  <div
                    key={cat.codCategoria}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0 4px 16px', fontSize: 13 }}
                  >
                    <span style={{ fontWeight: 500 }}>{cat.descricao}</span>
                    {cat.natureza && (
                      <Tag color={cat.natureza === 'R' ? 'blue' : 'red'} style={{ fontSize: 11 }}>
                        {cat.natureza === 'R' ? 'Receita' : 'Despesa'}
                      </Tag>
                    )}
                    <Tag color="warning" style={{ fontSize: 11 }}>
                      {cat.totalLancamentos} lanç.
                    </Tag>
                  </div>
                ))}
              </div>
            ))}
          </>
        ),
      }]}
    />
  );
}

function agruparPorPai(categorias) {
  if (!Array.isArray(categorias)) return [];
  const mapa = new Map();
  for (const cat of categorias) {
    const pai = cat.codCategoriaSup ?? '__raiz__';
    if (!mapa.has(pai)) {
      mapa.set(pai, { pai, descricaoPai: cat.descricaoCatSup || null, categorias: [] });
    }
    mapa.get(pai).categorias.push(cat);
  }
  return [...mapa.values()];
}
